import { useTranslation } from "react-i18next";
import type { TimeAttendanceStepProps } from "@/types/timeAttendance";
import { useState, useEffect } from "react";
import { MapPin, CircleCheck, CircleX, MapPinCheck } from "lucide-react";
import MapviewContainer from "../MapviewContainer";
import {
  formatAccuracy,
  getCurrentLocation,
  isLocationPermissionDenied,
  type ILocationFix,
} from "@/utils/geolocation";

type LocationState = "idle" | "requesting" | "verifying" | "success" | "error";

interface LocationData {
  latitude: number;
  longitude: number;
  address?: string;
}

const TimeAttendanceRuleShareLocationSteps: React.FC<
  TimeAttendanceStepProps
> = ({
  onNext,
  eligibility,
  handleCheckEligibilityAttendanceRule,
  setWithoutLocation,
}) => {
  const { t } = useTranslation(["common", "my-space"]);
  const [locationState, setLocationState] = useState<LocationState>("idle");
  const [location, setLocation] = useState<LocationData | null>(null);
  const [hasAttempted, setHasAttempted] = useState(false);
  // Only a real denial is worth sending the user to their permission settings;
  // a timeout or "position unavailable" with permission granted is not.
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);

  // Check if location already exists in eligibility
  useEffect(() => {
    if (
      eligibility?.location?.userLocation?.latitude &&
      eligibility?.location?.userLocation?.longitude
    ) {
      setLocationState("success");
      setLocation({
        latitude: Number(eligibility.location.userLocation.latitude),
        longitude: Number(eligibility.location.userLocation.longitude),
        address: eligibility.location.address,
      });
    }
  }, [eligibility]);

  const requestLocationAccess = async () => {
    setHasAttempted(true);
    setLocationState("requesting");

    let fix: ILocationFix;
    try {
      fix = await getCurrentLocation();
    } catch (error) {
      setIsPermissionDenied(isLocationPermissionDenied(error));
      setLocationState("error");
      return;
    }

    setLocationState("verifying");
    setLocation({ latitude: fix.lat, longitude: fix.long });

    // Call the eligibility check with the new coordinates
    try {
      await handleCheckEligibilityAttendanceRule?.(fix);
      setLocationState("success");
    } catch (error) {
      console.error("Eligibility check failed:", error);
      setLocationState("error");
    }
  };

  const isLocationVerified = eligibility?.location?.isVerified;

  const handleContinue = () => {
    if (locationState === "success") {
      onNext?.();
    }
  };

  // Initial state - before user clicks button
  if (
    locationState === "idle" &&
    !hasAttempted &&
    !eligibility?.location?.userLocation?.latitude
  ) {
    return (
      <div className="flex flex-col items-center p-6">
        <div className="relative flex justify-center mb-6 w-full h-80">
          <div className="w-full h-full rounded-xs bg-border flex items-center justify-center">
            <MapPin size={48} className="text-gray-400" />
          </div>
          <p className="absolute bg-white px-2 py-1 rounded-xs bottom-2 text-center text-xs text-gray-500 mb-1">
            {t("my-space:checkin.allowed_radius_value", {
              meters: eligibility?.location?.radius || 0,
            })}
          </p>
        </div>

        <h3 className="text-lg font-semibold text-center mb-1">
          {t("my-space:checkin.location_access_required")}
        </h3>

        <p className="text-sm max-w-xs text-gray-500 text-center mb-6">
          {t("my-space:checkin.allow_location_to_verify")}
        </p>

        <button
          onClick={requestLocationAccess}
          className="w-full h-11 rounded-xs bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition"
        >
          {t("my-space:checkin.allow_location_access")}
        </button>
      </div>
    );
  }

  // Error state - after user denied or error occurred
  if (locationState === "error" && hasAttempted) {
    return (
      <div className="flex flex-col items-center p-6">
        {/* Location Icon */}
        <span className="p-3 size-max rounded-full bg-orange-100 flex justify-center items-center mb-4">
          <MapPinCheck size={38} className="text-orange-600" />
        </span>

        <h3 className="text-lg font-semibold text-center mb-2">
          {isPermissionDenied
            ? t("my-space:checkin.please_enable_location_permissions")
            : t("my-space:checkin.couldnt_get_your_location")}
        </h3>

        <p className="text-sm max-w-xs text-gray-500 text-center mb-4">
          {isPermissionDenied
            ? t("my-space:checkin.unable_to_access_location")
            : t("my-space:checkin.device_did_not_return_location")}
        </p>

        {/* Illustration */}
        {isPermissionDenied && (
          <img
            width={400}
            height={200}
            alt={t("my-space:checkin.change_browser_location_settings")}
            src="/illustrations/grant-location-permission.svg"
            className="mb-4"
          />
        )}
        {/* Action Buttons */}
        <div className="w-full space-y-2">
          {isPermissionDenied && (
            <p className="text-center text-sm text-gray-500 font-semibold">
              {t("my-space:checkin.location_permission_enabled")}
            </p>
          )}
          <button
            onClick={requestLocationAccess}
            className="w-full h-11 rounded-xs bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition"
          >
            {t("my-space:checkin.retry_location_access")}
          </button>
          <button
            onClick={() => {
              setWithoutLocation?.(true);
              onNext?.();
            }}
            className="w-full h-11 rounded-xs border border-indigo-600 text-primary font-semibold hover:bg-primary/15 transition"
          >
            {t("my-space:checkin.continue_without_location")}
          </button>
        </div>
      </div>
    );
  }

  // Verifying State
  if (locationState === "requesting" || locationState === "verifying") {
    return (
      <div className="flex flex-col gap-1 items-center bg-primary/10 p-10">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="size-20 rounded-full bg-white flex items-center justify-center">
              <MapPin size={32} className="text-indigo-500" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="size-20 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-center mb-2">
          {locationState === "requesting"
            ? t("my-space:checkin.checking_location")
            : t("my-space:checkin.verifying_location")}
        </h3>

        <p className="text-sm max-w-xs text-gray-500 text-center mb-6">
          {locationState === "requesting"
            ? t("my-space:checkin.please_wait_checking_location")
            : t("my-space:checkin.please_wait_verifying_location")}
        </p>
      </div>
    );
  }

  // Success State with Map (show for both new capture and existing location)
  if (locationState === "success" && location) {
    return (
      <div className="flex flex-col items-center p-6">
        {/* Map Container */}
        <div
          className={`h-[400px] w-full relative rounded-sm overflow-hidden mb-4 ${
            isLocationVerified
              ? "border-[1.8px] border-green-400"
              : "border-[1.8px] border-orange-400"
          }`}
        >
          <div
            className={`flex items-center ${
              isLocationVerified ? "bg-green-500/10" : "bg-orange-500/10"
            } p-2 gap-3`}
          >
            {/* Circle Icon */}
            <div className="relative flex items-center justify-center h-8 w-10 rounded-full bg-white">
              {isLocationVerified ? (
                <CircleCheck size={20} className="text-green-500" />
              ) : (
                <CircleX size={20} className="text-orange-500" />
              )}
            </div>

            {/* Text */}
            <div className="flex flex-col gap-0.5">
              <span
                className={`text-sm font-medium ${
                  isLocationVerified ? "text-green-500" : "text-orange-500"
                }`}
              >
                {eligibility?.location?.message ||
                  (isLocationVerified
                    ? t("my-space:checkin.location_verified")
                    : t("my-space:checkin.location_outside_radius"))}
              </span>
              <span className="text-xs text-black/50">
                {eligibility?.location?.address ?? "-"}
              </span>
            </div>
          </div>
          <MapviewContainer
            radius={eligibility?.location?.radius || 0}
            selectedLocation={
              eligibility?.location?.permissedLocation
                ? {
                    lat: Number(
                      eligibility?.location?.permissedLocation.latitude,
                    ),
                    lng: Number(
                      eligibility?.location?.permissedLocation.longitude,
                    ),
                    name: t("my-space:checkin.permitted_location"),
                  }
                : undefined
            }
            personLocation={
              eligibility?.location?.userLocation
                ? {
                    lat: Number(eligibility?.location?.userLocation.latitude),
                    lng: Number(eligibility?.location?.userLocation.longitude),
                    name: `${eligibility?.location?.userLocation?.name}`,
                    accuracy: eligibility?.location?.accuracy,
                  }
                : undefined
            }
            distance={eligibility?.location?.distance ?? 0}
            isDraggable={false}
            className="rounded-none rounded-b-sm! h-full"
          />
          <div className="absolute bottom-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 bg-white p-2 rounded-md shadow-xs flex gap-4 z-10 text-xs">
            <p className="text-black/50">
              {t("my-space:checkin.allowed_radius_label")}{" "}
              <span className="text-green-500">
                {t("common:shared_ui.meters_short", {
                  meters: Math.round(eligibility?.location?.radius || 0),
                })}
              </span>
            </p>
            <p className="text-black/50">
              {t("my-space:checkin.distance_label")}{" "}
              <span
                className={
                  isLocationVerified ? "text-green-500" : "text-orange-500"
                }
              >
                {t("common:shared_ui.meters_short", {
                  meters: Math.round(eligibility?.location?.distance || 0),
                })}
              </span>
            </p>
            {formatAccuracy(eligibility?.location?.accuracy) && (
              <p
                className="text-black/50"
                title={t("my-space:checkin.accuracy_hint")}
              >
                {t("my-space:checkin.accuracy_label")}{" "}
                <span className="text-gray-700">
                  {formatAccuracy(eligibility?.location?.accuracy)}
                </span>
              </p>
            )}
          </div>
        </div>

        <h3 className="text-lg font-semibold text-center mb-1">
          {isLocationVerified
            ? t("my-space:checkin.location_verified_title")
            : t("my-space:checkin.location_captured")}
        </h3>

        <p className="text-sm text-gray-500  text-center mb-6">
          {isLocationVerified
            ? t("my-space:checkin.location_successfully_verified")
            : t("my-space:checkin.couldnt_verify_location_submit")}
        </p>

        <button
          onClick={handleContinue}
          className="w-full h-11 rounded-xs bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition"
        >
          {t("common:buttons.continue")}
        </button>
      </div>
    );
  }

  return null;
};

export default TimeAttendanceRuleShareLocationSteps;
